import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import { OverflowMenu, Shimmer, TabPanel, Tabs } from '@paypalcorp/pp-react'
import { sharePermissionConstants } from '../../constants/sharePermissionConstants'
import {
  getPermissionRequestTableHeaders,
  renderRequestStatus,
  renderStatusMsg,
  truncateLongText,
} from './helpers'
import RequestSharePermission from './RequestSharePermission'
import Dropdown from '../Dropdown'
import InputBox from '../InputBox'
import Datepicker from '../Datepicker'
import RenderRequestDetails from './RenderRequestDetails'
import AlertBox from '../AlertBox'
import {
  ACTION_TEXT,
  ENDPOINT,
  handleSharePermissionRequestActions,
} from './apiUtils'
import Pagination from '../Pagination'
import { apiErrorMessages } from './apiErrorMessages'

const PermissionRequestHome = (props) => {
  const urlState = props.location?.state
  const {
    permissionRequestList: {
      tabDetails,
      tableHeader,
      statusPerTab,
      searchOptions,
      filterOptions,
      labels,
      requestAccess,
      requestPerPage,
      pageSize,
      isTotalRequired,
    },
    requestedDetailsActions,
    requestDetailsLabels,
    Heading,
    RequestIdFormat,
  } = sharePermissionConstants

  const [requestAccessBoxVisibility, setRequestAccessBoxVisibility] = useState(
    urlState?.showPermissionBox || false,
  )
  const [requestDetailsForId, setRequestDetailsForId] = useState('')
  const [isEditRequest, setIsEditRequest] = useState(false)
  const [isViewRequest, setIsViewRequest] = useState(false)
  const [isApproveRejectRequest, setIsApproveRejectRequest] = useState('')
  const [requestDetailsVisibility, setRequestDetailsVisibility] = useState(
    false,
  )
  const [requestDetails, setRequestDetails] = useState({})
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  const [tableHeaders, setTableHeaders] = useState(
    getPermissionRequestTableHeaders(
      tableHeader,
      tabDetails.tabs[selectedTabIndex].id,
    ),
  )
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false)
  const [actionMenuOpenFor, setActionMenuOpenFor] = useState(null)

  // Search functionality state
  const [searchType, setSearchType] = useState('request_id')
  const [searchValue, setSearchValue] = useState('')

  const [alertBoxVisibility, setAlertBoxVisibility] = useState(false)
  const [alertBoxData, setAlertBoxData] = useState({
    status: false,
    msg: '',
    sec_msg: '',
    action: [],
    for: {},
    type: '',
    showSpinner: false,
  })

  // Filter functionality state
  const [filters, setFilters] = useState({
    status: 'all',
  })

  // Date filter state
  const today = new Date()
  const lastMonth = new Date()
  lastMonth.setMonth(today.getMonth() - 1) // subtract 1 month

  const [dateFilter, setDateFilter] = useState({
    startDate: lastMonth,
    endDate: today,
    key: 'selection',
  })
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [isFetchListInProgress, setIsFetchListInProgress] = useState(false)
  const [allRequestList, setAllRequestList] = useState([])
  const [pageNo, setPageNo] = useState(1)
  const [statusMessage, setStatusMessage] = useState({
    msg: '',
    status: 'success',
  })

  const [
    requestDetailsStatusMessage,
    setRequestDetailsStatusMessage,
  ] = useState({
    msg: '',
    status: 'success',
  })

  // Format a date (Date or string) to "YYYY-MM-DDTHH:mmZ" in UTC
  const toIsoMinuteZ = (v) => {
    const d = new Date(v)
    if (Number.isNaN(d)) return v
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
      d.getUTCDate(),
    )}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}Z`
  }

  const fetchCompleteRequestList = async (
    pageNumber,
    requestId = '',
    selectedTab = tabDetails.tabs[selectedTabIndex].id,
  ) => {
    setIsFetchListInProgress(true)
    try {
      const statuses = statusPerTab[selectedTab].join(',')
      const params = {
        page: pageNumber,
        page_size: pageSize,
        total_required: isTotalRequired,
        update_after: toIsoMinuteZ(dateFilter.startDate),
        update_before: toIsoMinuteZ(dateFilter.endDate),
        statuses,
        ...(selectedTab === 'approvals' && { is_approver: true }),
        ...(selectedTab === 'my_requests' && { is_requestor: true }),
      }

      const response = await handleSharePermissionRequestActions(
        '',
        params,
        'getAll',
      )
      if (response?.status === 'success') {
        const requests = response?.data?.requests || []
        setAllRequestList(requests)
        if (requestId !== '') {
          const request = requests.find((r) => r.request_id === requestId)
          if (request) {
            setRequestDetails(request)
            setRequestDetailsVisibility(true)
          }
        }
      } else {
        setStatusMessage({ msg: response.msg, status: response.status })
      }
    } catch (error) {
      console.error(error)
      setStatusMessage({
        msg: sharePermissionConstants.statusMsg.requestActionError.replace(
          '{operation}',
          'fetching',
        ),
        status: 'error',
      })
    } finally {
      setIsFetchListInProgress(false)
    }
  }

  useEffect(() => {
    // Fetch request details directly using request id from URL
    if (props?.requestId) {
      setRequestDetailsForId(props?.requestId)
      setRequestDetailsVisibility(true)
    }
    // Fetch initial data
    fetchCompleteRequestList(pageNo)
  }, [])

  const setSelectedTabDetails = (_evt, index) => {
    const tableHeadersList = getPermissionRequestTableHeaders(
      tableHeader,
      tabDetails.tabs[index].id,
    )
    setTableHeaders(tableHeadersList)
    setSelectedTabIndex(index)
    // Reset search and filters when switching tabs
    setSearchValue('')
    setFilters({
      status: 'all',
    })
    setDateFilter({
      startDate: lastMonth,
      endDate: today,
      key: 'selection',
    })
    setStatusMessage({ msg: '', status: 'success' })

    /* Fetch all request on tab change*/
    setPageNo(1)
    fetchCompleteRequestList(1, '', tabDetails.tabs[index].id)
  }

  const handleSearchTypeChange = (selectedIndex) => {
    const value = searchOptions[selectedIndex].value
    setSearchType(value)
  }

  const handleSearchValueChange = (e) => {
    setSearchValue(e.target.value)
  }

  const handleFilterChange = (filterType, selectedTab) => (selectedIndex) => {
    const value = filterOptions[filterType].filter((status) =>
      status.renderFor.includes(selectedTab),
    )[selectedIndex].value

    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }))
  }

  const handleDateFilterChange = (ranges) => {
    setDateFilter(ranges.selection)
  }

  const handleDateSubmit = () => {
    setShowDatePicker(false)
    fetchCompleteRequestList(1)
  }

  const handleClearDate = () => {
    setDateFilter({
      startDate: lastMonth,
      endDate: today,
      key: 'selection',
    })
    setShowDatePicker(false)
  }

  const handleActionMenuToggle = (status, actionMenuFor) => {
    setIsActionMenuOpen(status)
    setActionMenuOpenFor(actionMenuFor)
  }

  const showAlert = (
    display = false,
    { request, type = 'info', msg = [], actions = [], showSpinner = false },
  ) => {
    setAlertBoxData({
      status: display,
      msg: Array.isArray(msg) ? msg : [String(msg ?? '')],
      sec_msg: '',
      action: actions,
      for: request,
      type,
      showSpinner,
    })
    setAlertBoxVisibility(display)
  }

  /**
   * Handles share-permission request actions like "withdraw" or "resume".
   * Expects:
   * - request: { request_id: string, ... }
   * - action:  "withdraw" | "resume" | string
   */
  const handleShareRequestAction = async (request, action) => {
    const requestId = request?.request_id
    const endpoint = ENDPOINT?.[action]

    // Map actions to human-friendly verbs for messages
    const verb = ACTION_TEXT[action] ?? {
      prog: 'processing',
      past: 'processed',
    }

    // Guard rails for missing inputs
    if (!requestId) {
      showAlert(true, {
        type: 'error',
        msg: apiErrorMessages.GENERAL.missingRequestId,
        actions: ['close'],
      })
      return
    }
    if (!endpoint) {
      showAlert(true, {
        type: 'error',
        msg: apiErrorMessages.GENERAL.unsupportedAction.replace(
          '{action}',
          action,
        ),
        actions: ['close'],
      })
      return
    }

    // Initial “working” state
    showAlert(true, {
      request,
      type: 'info',
      msg: [
        sharePermissionConstants.statusMsg.requestProcessing.replace(
          '{operation}',
          verb.prog,
        ),
      ],
      showSpinner: true,
    })

    try {
      const path = `${requestId}${endpoint}`
      const response = await handleSharePermissionRequestActions(
        path,
        {},
        action,
      )
      const isSuccess = response?.status === 'success'
      showAlert(true, {
        request,
        type: isSuccess ? 'success' : 'error',
        msg:
          response?.message ||
          response?.msg ||
          apiErrorMessages.GENERAL.unknownError,
        actions: ['close'],
        showSpinner: false,
      })
    } catch (error) {
      console.error(error)
      showAlert(true, {
        request,
        type: 'error',
        msg: sharePermissionConstants.statusMsg.requestActionError.replace(
          '{operation}',
          verb.prog,
        ),
        actions: ['close'],
        showSpinner: false,
      })
    }
  }

  const updateActionHandler = (request) => {
    setRequestDetailsVisibility(false)
    setRequestAccessBoxVisibility(true)
    setIsEditRequest(true)
    setRequestDetails(request)
  }

  const handleActionMenuChange = (evt, request) => {
    const action = evt?.target?.value
    if (!action) return

    const showDetails = () => {
      setRequestDetailsVisibility(true)
      setRequestDetails(request)
    }

    const renderAlert = () => {
      showAlert(true, {
        request,
        type: 'warning',
        msg: [
          sharePermissionConstants.statusMsg.requestActionWarning.replace(
            '{operation}',
            action,
          ),
        ],
        actions: ['cancel', action],
        showSpinner: false,
      })
    }

    const handlers = {
      update: () => {
        updateActionHandler(request)
      },
      view: () => {
        showDetails()
        setIsViewRequest(true)
      },
      approve: () => {
        showDetails()
        setIsApproveRejectRequest('approve')
      },
      reject: () => {
        showDetails()
        setIsApproveRejectRequest('reject')
      },
      withdraw: () => {
        renderAlert()
      },
      resume: () => {
        renderAlert()
      },
    }

    const handler = handlers[action]
    if (handler && typeof handler === 'function') {
      handler()
    } else {
      console.error(`No handler found for action: ${action}`)
    }
  }

  const handleAlertBoxAction = (request, action) => {
    if (action === 'cancel') {
      showAlert(false, {})
      return
    }
    if (action === 'close') {
      showAlert(false, {})
      fetchCompleteRequestList(1)
      return
    }
    handleShareRequestAction(request, action)
  }

  const renderLocalizedAction = (action) => {
    return action
  }

  const renderActions = (columnKey, columnIndex, request, requestIndex) => {
    const actions = requestedDetailsActions
      .filter(
        (action) =>
          action.visibleForTab.includes(tabDetails.tabs[selectedTabIndex].id) &&
          action.visibleForStatus.includes(request.status) &&
          action.isVisible,
      )
      .map(({ className: _c, isSecondary: _s, ...rest }) => rest)

    return (
      <div key={columnIndex} className={tableHeaders[columnKey].className}>
        <OverflowMenu
          data-test-id={`overflow_menu_${requestIndex}`}
          name={`overflow_menu_${requestIndex}`}
          onOpen={() => handleActionMenuToggle(true, requestIndex)}
          onClose={() => handleActionMenuToggle(false, null)}
          onChange={(evt) => handleActionMenuChange(evt, request)}
          isOpen={isActionMenuOpen && actionMenuOpenFor === requestIndex}
          options={actions}
        />
      </div>
    )
  }

  const withTooltip = (text, columnIndex, className, threshold = 100) => {
    text = text ?? '-'
    const showTip = typeof text === 'string' && text.length > threshold
    return (
      <div
        key={columnIndex}
        className={`tooltip ${className} ${showTip ? 'cursor-pointer' : ''}`}
      >
        {truncateLongText(text, threshold)}
        {showTip && (
          <span className="tooltiptext permission__request-list-items_tooltiptext">
            {text}
          </span>
        )}
      </div>
    )
  }

  const formatUTCDateTime = (value) => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    const time = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    })
    return `${date.toDateString()}, ${time}`
  }

  const formatDuration = (value) => {
    if (value === null || value === '') return '-'
    const valueInNumeric = Number(value)
    if (!Number.isFinite(valueInNumeric)) return '-'
    const label =
      valueInNumeric > 1
        ? requestDetailsLabels.daysText
        : requestDetailsLabels.dayText
    return `${valueInNumeric} ${label}`
  }

  const renderColumnValue = (columnKey, columnIndex, request, requestIndex) => {
    const headerName = tableHeaders?.[columnKey] ?? {}
    const className = headerName.className ?? ''
    const value = request?.[headerName.key]

    switch (columnKey) {
      case 'id':
        return (
          <div key={columnIndex} className={className}>
            <a className="ppvx_link pointer">{value || '-'}</a>
          </div>
        )

      case 'sharee_email_addresses': {
        const emails = Array.isArray(value) ? value.join(', ') : '-'
        return withTooltip(emails, columnIndex, className, 60)
      }

      case 'status':
        return (
          <div
            key={columnIndex}
            className={`${className} permission__request-list-status`}
          >
            {renderRequestStatus(value)}
          </div>
        )

      case 'action':
        return renderActions(columnKey, columnIndex, request, requestIndex)

      case 'duration_in_days':
        return (
          <div key={columnIndex} className={className}>
            {formatDuration(value)}
          </div>
        )

      case 'created_at':
      case 'last_updated_at':
        return (
          <div key={columnIndex} className={className}>
            {formatUTCDateTime(value)}
          </div>
        )

      default: {
        const text = typeof value === 'string' ? value : value ?? '-'
        return withTooltip(text, columnIndex, className, 100)
      }
    }
  }

  const filterRequests = (availableRequests) => {
    let filteredRequests = availableRequests

    // Apply filters first
    if (filters.status !== 'all') {
      filteredRequests = filteredRequests.filter(
        (request) => request.status === filters.status,
      )
    }

    // Apply search if there's a search value
    if (searchValue) {
      filteredRequests = filteredRequests.filter((request) => {
        const fieldValue = request[searchType]

        if (searchType === 'sharee_email_addresses') {
          // For email addresses array, check if any email contains the search value
          return fieldValue?.some((email) =>
            email.toLowerCase().includes(searchValue.toLowerCase()),
          )
        }

        // For other fields, convert to string and check if it contains the search value
        return fieldValue
          ?.toString()
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      })
    }

    return filteredRequests
  }

  const renderRequestListLoading = () => {
    const shimmer = []
    Array.from({ length: 5 }).forEach((_value, index) => {
      shimmer.push(
        <li
          key={index}
          id={`permission__request-list-${index}`}
          className="permission__request-list"
        >
          <div className="permission__request-list-content ppvx_text--md">
            {Object.keys(tableHeaders).map((columnKey, columnIndex) => {
              return (
                <div
                  key={columnIndex}
                  className={tableHeaders[columnKey].className}
                >
                  <Shimmer className="shimmer" />
                </div>
              )
            })}
          </div>
        </li>,
      )
    })
    return shimmer
  }

  const renderRequestList = (filteredRequests) => {
    return filteredRequests.length > 0 ? (
      filteredRequests.map((request, requestIndex) => {
        return (
          <li
            key={requestIndex}
            className="permission__request-list "
            id={`permission__request-list-${requestIndex}`}
            data-test-id={`permission__request-list-${requestIndex}`}
          >
            <div className="permission__request-list-content ppvx_text--md">
              {Object.keys(tableHeaders).map((columnKey, columnIndex) => {
                return renderColumnValue(
                  columnKey,
                  columnIndex,
                  request,
                  requestIndex,
                )
              })}
            </div>
          </li>
        )
      })
    ) : (
      <li className="permission__request-list dFlexCenter">
        <div className="permission__request-list-no-content ppvx_text--md dFlexCenter">
          No requests found
        </div>
      </li>
    )
  }

  const renderListHeader = () => {
    const tableHeadersList = Object.keys(tableHeaders)
    return (
      <div className="permission__request-list_header ">
        {tableHeadersList.length > 0 &&
          tableHeadersList.map((header, index) => {
            return (
              <div key={index} className={tableHeaders[header].className}>
                {tableHeaders[header].label}
              </div>
            )
          })}
      </div>
    )
  }

  const renderDatePickerFilter = () => {
    return (
      <div className="permission__request-filter-item permission__request-date-filter">
        <label className="permission__request-date-label">
          {labels.dateRange}
        </label>
        <div className="permission__request-date-picker-container">
          <button
            className="permission__request-date-picker-button"
            onClick={() => setShowDatePicker(!showDatePicker)}
          >
            {`${dateFilter.startDate.toLocaleDateString()} - ${dateFilter.endDate.toLocaleDateString()}`}
          </button>
          {showDatePicker && (
            <div className="permission__request-date-picker-dropdown">
              <Datepicker
                selectionRange={dateFilter}
                handleSelect={handleDateFilterChange}
                handleDateSubmit={handleDateSubmit}
                handleClearDate={handleClearDate}
                user={props.user || {}}
                minDate={filterOptions.dateFilter.minDate}
                className={'permission__request-date_picker'}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderStatusFilter = () => {
    const selectedTab = tabDetails.tabs[selectedTabIndex].id
    const statusOption = filterOptions.status.filter((option) => {
      return option.renderFor.includes(selectedTab)
    })
    return (
      statusOption.length > 0 && (
        <div className="permission__request-filter-item">
          <Dropdown
            label="Status"
            options={statusOption}
            value={filters.status}
            handleOnSelect={handleFilterChange('status', selectedTab)}
            className="permission__request-filter-dropdown"
          />
        </div>
      )
    )
  }

  const renderSearchBar = () => {
    return (
      <div className="permission__request-search-section">
        <div className="permission__request-search-type">
          <Dropdown
            label="Search Type"
            options={searchOptions}
            value={searchType}
            handleOnSelect={handleSearchTypeChange}
            className="permission__request-search-dropdown"
          />
        </div>
        <div className="permission__request-search-input">
          <InputBox
            name="searchValue"
            id="searchValue"
            placeholder="Enter search value..."
            value={searchValue}
            onChangeText={handleSearchValueChange}
            onKeyPressText={() => {}}
            onBlurText={() => {}}
            className="permission__request-search-textbox"
            isLabelHidden={true}
          />
        </div>
      </div>
    )
  }

  const renderSearchAndFilterOptions = () => {
    return (
      <div className="permission__request-search-container">
        <div className="permission__request-search-and-filter-bar">
          {/* Search Section */}
          {renderSearchBar()}
          {/* Filter Section */}
          <div className="permission__request-filter-section">
            {renderStatusFilter()}
            {renderDatePickerFilter()}
          </div>
        </div>
      </div>
    )
  }

  const onPaginationClick = (pageNumber) => {
    setPageNo(pageNumber)
  }

  const removeRequestIdFromUrl = () => {
    const { pathname, search } = window.location
    // Regex: matches the UUID-like requestId at the end of the path
    const cleanedPath = pathname.replace(RequestIdFormat, '')
    // Update URL without reload
    window.history.replaceState({}, '', cleanedPath + search)
  }

  const handleRequestDetailsClose = () => {
    setRequestDetailsVisibility(false)
    setRequestDetailsForId('')
    fetchCompleteRequestList(1)
    removeRequestIdFromUrl()
    setRequestDetailsStatusMessage({ msg: '', status: 'success' })
  }

  const setSharePermissionBoxVisibility = (status, request) => {
    setRequestAccessBoxVisibility(status)
    setRequestDetails(request)
    setRequestDetailsStatusMessage({ msg: '', status: 'success' })
  }

  const setSubmitRequestSuccess = (request, message) => {
    setRequestDetails(request)
    setRequestDetailsStatusMessage(message)
    setRequestAccessBoxVisibility(false)
    setRequestDetailsVisibility(true)
  }

  const renderTabPanelContent = () => {
    const username = props?.user?.username ?? ''
    const selectedTab = tabDetails?.tabs?.[selectedTabIndex]
    const statuses = statusPerTab?.[selectedTab?.id] ?? []

    let roleKey = ''
    if (selectedTabIndex === 0) {
      roleKey = 'requestor'
    } else if (selectedTabIndex === 1) {
      roleKey = 'approver'
    } else {
      roleKey = 'both'
    }

    const availableRequests = (allRequestList ?? []).filter((request) => {
      if (roleKey === 'both') {
        return (
          statuses.includes(request?.status) &&
          (request?.requestor === username || request?.approver === username)
        )
      }
      return (
        statuses.includes(request?.status) && request?.[roleKey] === username
      )
    })

    const filteredRequests = filterRequests(availableRequests)
    const totalFiltered = filteredRequests.length
    const totalPages = Math.ceil(totalFiltered / requestPerPage)

    const min = pageNo > 1 ? (pageNo - 1) * requestPerPage : 0
    const max = pageNo * requestPerPage
    const requestToRender = filteredRequests.slice(min, max)

    return (
      <div>
        {renderSearchAndFilterOptions()}
        <ul
          tabIndex={selectedTabIndex}
          className="permission__request_list_container"
          data-test-id={`permission__request_list_container-${selectedTab.id}-${
            isFetchListInProgress ? 'loading' : 'data'
          }`}
        >
          <li className="permission__request-list">
            <div className="permission__request-list-content ppvx_text--md">
              {renderListHeader()}
            </div>
          </li>
          {isFetchListInProgress
            ? renderRequestListLoading()
            : renderRequestList(requestToRender)}
          {!isFetchListInProgress && (
            <div>
              {totalPages > 1 && (
                <Pagination
                  totalPages={totalPages}
                  pageNo={pageNo}
                  onPaginationClick={onPaginationClick}
                  searchQuery={{}}
                  totalElements={totalFiltered}
                  pageLimit={requestPerPage}
                />
              )}
            </div>
          )}
        </ul>
      </div>
    )
  }

  const renderTabsPannel = (tabName) => {
    return (
      <TabPanel
        id={`${tabName}_panel`}
        tabId={`${tabName}_panel`}
        data-test-id={`${tabName}_panel`}
      >
        {renderTabPanelContent()}
      </TabPanel>
    )
  }

  const renderTabs = () => {
    return (
      <Tabs
        name={tabDetails.name}
        selectedTabIdx={selectedTabIndex}
        tabs={tabDetails.tabs}
        onClick={setSelectedTabDetails}
        className="permission__request_tabs"
      >
        {selectedTabIndex === 0 ? renderTabsPannel('my_requests') : ''}
        {selectedTabIndex === 1 ? renderTabsPannel('approvals') : ''}
        {selectedTabIndex === 2 ? renderTabsPannel('completed_requests') : ''}
      </Tabs>
    )
  }

  const renderRequestLink = () => {
    return (
      <div className="request_link__container">
        <a
          className="ppvx_link flexAlignCenter pointer"
          onClick={() =>
            setRequestAccessBoxVisibility(!requestAccessBoxVisibility)
          }
        >
          <span className="ppvx_icon ppvx_icon--size_xs ppvx_icon-add refresh__icon" />
          <span className="hide_mobile">{requestAccess.label}</span>
        </a>
      </div>
    )
  }

  const renderPageTitle = () => {
    return (
      <div className="permission__request_header">
        <h1>{Heading}</h1>
        {renderRequestLink()}
      </div>
    )
  }

  const renderRequestDetails = () => {
    return (
      <RenderRequestDetails
        {...props}
        showPermissionBox={requestDetailsVisibility}
        requestDetailsForId={requestDetailsForId}
        requestDetails={requestDetails}
        onClose={() => handleRequestDetailsClose()}
        isViewRequest={isViewRequest}
        isApproveRejectRequest={isApproveRejectRequest}
        statusMsg={requestDetailsStatusMessage}
        updateActionHandler={updateActionHandler}
      />
    )
  }

  const renderRequestAccessBox = () => {
    return (
      <RequestSharePermission
        {...props}
        showPermissionBox={requestAccessBoxVisibility}
        requestDetailsFromServer={isEditRequest ? requestDetails : {}}
        isEditRequest={isEditRequest}
        setSharePermissionBoxVisibility={setSharePermissionBoxVisibility}
        handleRequestDetailsClose={handleRequestDetailsClose}
        setSubmitRequestSuccess={setSubmitRequestSuccess}
      />
    )
  }

  return (
    <div className="permission__request_home">
      <div className="permission__request_list_content">
        {renderPageTitle()}
        {(statusMessage?.msg || '') !== '' && renderStatusMsg(statusMessage)}
        {renderTabs()}
      </div>
      {requestAccessBoxVisibility && renderRequestAccessBox()}
      {requestDetailsVisibility && renderRequestDetails()}
      {alertBoxVisibility && (
        <AlertBox
          {...props}
          alertBox={alertBoxData}
          checkAlertBoxAction={handleAlertBoxAction}
          renderLocalizedAction={renderLocalizedAction}
        />
      )}
    </div>
  )
}
export default PermissionRequestHome
